import { createEffect, createEvent, createStore, sample } from 'effector';
import { AxiosError } from 'axios';
import { axiosClient } from '@nx-frontend/shared/api';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { Workspace } from '@nx-frontend/shared/types';
import { roundHalfToEven } from '@nx-frontend/shared/utils';

// интервал через который проверяется статус задачи
const CHECK_TRANSCIPT_JOB_INTERVAL = 10e3;
// тамаут на удаление интервала проверки статуса задачи
const CHECK_TRANSCRIPT_JOB_TIMEOUT = 60 * 60e3;

export interface TranscriptPanelState {
  opened: boolean;
  visible: boolean;
  attachment: TranscriptPanelAttachment;
  entities: Record<string, TranscriptPanelEntity>;
}

export interface TranscriptPanelEntity {
  status: TranscriptStatus;
  annotations: TranscriptPanelAnnotation[];
  jobId: string | null;
}

export enum TranscriptStatus {
  NONE = 'none',
  PANDING = 'panding',
  IN_PROGRESS = 'processing',
  ERROR = 'failed',
  DONE = 'done',
  LIMIT_ERROR = 'limit-error',
}

export interface TranscriptPanelAnnotation {
  description: string;
  stt: {
    end: string;
    speaker: string;
    start: string;
  };
}

export const initialTranscriptPanelState: TranscriptPanelState = {
  opened: false,
  visible: false,
  attachment: {
    globalId: '',
    url: '',
    displayName: '',
    duration: 0,
  },
  entities: {},
};

export interface TranscriptPanelAttachment {
  globalId: string;
  url: string;
  displayName: string;
  aiCommand?: boolean;
  duration: number;
}

export interface RemoveTranscriptItemPayload {
  workspaceId: string;
  attachmentId: string;
  index: number;
}

interface EditSpeakerPayload {
  oldSpeaker: string;
  newSpeaker: string;
  workspaceId: string;
  attachmentId: string;
}
interface EditDescriptionPayload {
  index: number;
  newValue: string;
  workspaceId: string;
  attachmentId: string;
}

export const removeTranscriptItemEvent = createEvent<RemoveTranscriptItemPayload>();

export const editSpeakerEvent = createEvent<EditSpeakerPayload>();
export const editDescriptionEvent = createEvent<EditDescriptionPayload>();

export const updateAnnotationsEvent = createEvent<{
  attachmentId: string;
  updatedAnnotations: TranscriptPanelAnnotation[];
}>();

export const $transcriptPanel = createStore<TranscriptPanelState>(initialTranscriptPanelState);

export const hideTranscriptPanelEvent = createEvent();

export const showTranscriptPanelEvent = createEvent<TranscriptPanelAttachment>();

export const toggleTranscriptPanelVisibilityEvent = createEvent();

$transcriptPanel.on(hideTranscriptPanelEvent, (_) => ({
  ..._,
  opened: false,
  visible: false,
}));

$transcriptPanel.on(showTranscriptPanelEvent, (_, payload) => {
  let opened = true;
  let visible = true;
  if (!payload.aiCommand && _.attachment.globalId === payload.globalId) {
    opened = !_.visible && _.opened ? true : !_.opened;
    visible = !_.visible;
  }
  return {
    ..._,
    opened,
    visible,
    attachment: payload,
  };
});

const startTranscriptJobFx = createEffect(
  async (params: { workspaceId: string; attachmentGlobalId: string; minutes: number }) => {
    const { workspaceId, attachmentGlobalId, minutes } = params;
    let jobId: string | null = null;
    try {
      const { data } = await axiosClient.post(`/v2/api/transcript/jobs`, {
        workspaceId,
        attachmentGlobalId,
        minutes: minutes < 1 ? 1 : minutes,
      });
      jobId = data.jobId;
    } catch (err) {
      const error = err as AxiosError;
      if (error.response && error.response.status === 400) {
        if (error.response.data && (error.response.data as Record<string, string>).name === 'LimitError') {
          return { attachmentGlobalId, jobId, error: TranscriptStatus.LIMIT_ERROR };
        }
      }
      console.error(err);
    }
    return { attachmentGlobalId, jobId };
  }
);

const setTranscriptPanelEntityEvent = createEvent<{ attachmentGlobalId: string; entity: TranscriptPanelEntity }>();

sample({
  source: {
    transcriptPanel: $transcriptPanel,
    currentWorkspace: $currentWorkspace,
  },
  clock: showTranscriptPanelEvent,
  filter: ({ transcriptPanel }, payload) => {
    return transcriptPanel.entities[payload.globalId] == null;
  },
  fn: (state: { currentWorkspace: Workspace }, payload: TranscriptPanelAttachment) => {
    return {
      workspaceId: state.currentWorkspace.globalId,
      attachmentGlobalId: payload.globalId,
      minutes: roundHalfToEven(payload.duration / 60),
      entity: {
        status: TranscriptStatus.PANDING,
        annotations: [],
      },
    };
  },
  target: [startTranscriptJobFx, setTranscriptPanelEntityEvent],
});

$transcriptPanel.on(setTranscriptPanelEntityEvent, (state, payload) => {
  return {
    ...state,
    entities: {
      ...state.entities,
      [payload.attachmentGlobalId]: payload.entity,
    },
  };
});

interface TranscriptJob {
  id: string;
  status: TranscriptStatus;
  annotation: {
    textDetailed: TranscriptPanelAnnotation[];
  };
  attachmentGlobalId: string;
}

const getTranscriptJob = async (jobId: string | null): Promise<TranscriptJob | null> => {
  let job: TranscriptJob | null = null;
  try {
    const { data } = await axiosClient.get<{ job: TranscriptJob }>(`/v2/api/transcript/jobs/${jobId}`);
    job = data?.job;
  } catch (err) {
    console.error(err);
  }
  return job;
};

interface TranscriptJobToState extends TranscriptPanelEntity {
  attachmentGlobalId: string;
}

const transcriptJobToState = (job: TranscriptJob): TranscriptJobToState => {
  return {
    attachmentGlobalId: job.attachmentGlobalId,
    status: job.status,
    annotations: job.annotation.textDetailed,
    jobId: job.id,
  };
};

const intervals: Record<string, NodeJS.Timeout | number> = {};
const timeouts: Record<string, NodeJS.Timeout | number> = {};
const waitTranscriptJobFx = createEffect(
  async (payload: {
    attachmentGlobalId: string;
    jobId: string | null;
    error?: string;
  }): Promise<TranscriptJobToState> => {
    intervals[payload.attachmentGlobalId] && clearInterval(intervals[payload.attachmentGlobalId]);
    timeouts[payload.attachmentGlobalId] && clearTimeout(timeouts[payload.attachmentGlobalId]);
    if (payload.jobId) {
      const job = await getTranscriptJob(payload.jobId);
      if (job?.status === TranscriptStatus.DONE || job?.status === TranscriptStatus.ERROR) {
        return transcriptJobToState(job);
      }
      return await new Promise((resolve) => {
        intervals[payload.attachmentGlobalId] = setInterval(async () => {
          const job = await getTranscriptJob(payload.jobId);
          if (job?.status === TranscriptStatus.DONE || job?.status === TranscriptStatus.ERROR) {
            clearInterval(intervals[payload.attachmentGlobalId]);
            timeouts[payload.attachmentGlobalId] && clearTimeout(timeouts[payload.attachmentGlobalId]);
            resolve(transcriptJobToState(job));
          }
        }, CHECK_TRANSCIPT_JOB_INTERVAL);
        timeouts[payload.attachmentGlobalId] = setTimeout(() => {
          intervals[payload.attachmentGlobalId] && clearInterval(intervals[payload.attachmentGlobalId]);
        }, CHECK_TRANSCRIPT_JOB_TIMEOUT);
      });
    }
    return {
      jobId: payload.jobId,
      status: payload.error === TranscriptStatus.LIMIT_ERROR ? TranscriptStatus.LIMIT_ERROR : TranscriptStatus.ERROR,
      annotations: [],
      attachmentGlobalId: payload.attachmentGlobalId,
    };
  }
);

sample({
  clock: startTranscriptJobFx.doneData,
  fn: (payload) => payload,
  target: waitTranscriptJobFx,
});

$transcriptPanel.on(startTranscriptJobFx.doneData, (state, payload) => {
  const { attachmentGlobalId, jobId, error } = payload;
  return {
    ...state,
    entities: {
      ...state.entities,
      [attachmentGlobalId]: {
        jobId,
        status: jobId
          ? TranscriptStatus.PANDING
          : error === TranscriptStatus.LIMIT_ERROR
          ? TranscriptStatus.LIMIT_ERROR
          : TranscriptStatus.ERROR,
        annotations: [],
      },
    },
  };
});

$transcriptPanel.on(waitTranscriptJobFx.doneData, (state, payload) => {
  const { status, annotations } = payload;
  const attachmentGlobalId = payload.attachmentGlobalId;

  return {
    ...state,
    entities: {
      ...state.entities,
      [attachmentGlobalId]: {
        ...state.entities[attachmentGlobalId],
        status,
        annotations,
      },
    },
  };
});

export const retryTranscriptEvent = createEvent();

sample({
  source: {
    transcriptPanel: $transcriptPanel,
    currentWorkspace: $currentWorkspace,
  },
  clock: retryTranscriptEvent,
  fn: (state: { currentWorkspace: Workspace; transcriptPanel: TranscriptPanelState }) => {
    const { globalId, duration } = state.transcriptPanel.attachment;
    return {
      workspaceId: state.currentWorkspace.globalId,
      attachmentGlobalId: globalId,
      minutes: roundHalfToEven(duration / 60),
      entity: {
        status: TranscriptStatus.PANDING,
        annotations: [],
      },
    };
  },
  target: [startTranscriptJobFx, setTranscriptPanelEntityEvent],
});

$transcriptPanel.on(toggleTranscriptPanelVisibilityEvent, (state) => ({
  ...state,
  visible: !state.visible,
}));

export const saveChangesFx = createEffect(
  async ({
    attachmentId,
    updatedAnnotations,
    workspaceId,
  }: {
    attachmentId: string;
    updatedAnnotations: TranscriptPanelAnnotation[];
    workspaceId: string;
  }) => {
    try {
      await axiosClient.post(`/v2/api/workspaces/${workspaceId}/annotations/update`, {
        textDetailed: updatedAnnotations,
        attachmentId,
      });
    } catch (error) {
      console.error('Failed to save changes:', error);
      throw error;
    }
  }
);

$transcriptPanel.on(removeTranscriptItemEvent, (state, { attachmentId, index }) => {
  const updatedEntities = { ...state.entities };

  if (updatedEntities[attachmentId]?.annotations) {
    const updatedAnnotations = updatedEntities[attachmentId].annotations.filter((_, idx) => idx !== index);

    updatedEntities[attachmentId] = {
      ...updatedEntities[attachmentId],
      annotations: updatedAnnotations,
    };
  }

  return {
    ...state,
    entities: updatedEntities,
  };
});

sample({
  source: $transcriptPanel,
  clock: removeTranscriptItemEvent,
  fn: (state, { workspaceId, attachmentId }) => ({
    attachmentId,
    updatedAnnotations: state.entities[attachmentId]?.annotations ?? [],
    workspaceId,
  }),
  target: saveChangesFx,
});

$transcriptPanel.on(editSpeakerEvent, (state, { attachmentId, oldSpeaker, newSpeaker }) => {
  const updatedEntities = { ...state.entities };

  if (updatedEntities[attachmentId]?.annotations) {
    const updatedAnnotations = updatedEntities[attachmentId].annotations.map((annotation) => {
      if (annotation.stt.speaker === oldSpeaker) {
        return {
          ...annotation,
          stt: {
            ...annotation.stt,
            speaker: newSpeaker,
          },
        };
      }
      return annotation;
    });

    updatedEntities[attachmentId] = {
      ...updatedEntities[attachmentId],
      annotations: updatedAnnotations,
    };
  }

  return {
    ...state,
    entities: updatedEntities,
  };
});

sample({
  source: $transcriptPanel,
  clock: editSpeakerEvent,
  fn: (state, { workspaceId, attachmentId }) => ({
    attachmentId,
    updatedAnnotations: state.entities[attachmentId]?.annotations ?? [],
    workspaceId,
  }),
  target: saveChangesFx,
});

$transcriptPanel.on(editDescriptionEvent, (state, { attachmentId, index, newValue }) => {
  const updatedEntities = { ...state.entities };

  if (updatedEntities[attachmentId]?.annotations && updatedEntities[attachmentId].annotations[index]) {
    const updatedAnnotations = updatedEntities[attachmentId].annotations;
    updatedAnnotations[index].description = newValue;

    updatedEntities[attachmentId] = {
      ...updatedEntities[attachmentId],
      annotations: updatedAnnotations,
    };
  }

  return {
    ...state,
    entities: updatedEntities,
  };
});

sample({
  source: $transcriptPanel,
  clock: editDescriptionEvent,
  fn: (state, { workspaceId, attachmentId }) => ({
    attachmentId,
    updatedAnnotations: state.entities[attachmentId]?.annotations ?? [],
    workspaceId,
  }),
  target: saveChangesFx,
});

/**
 * Manage tabs.
 */
export const Tab = {
  Transcript: 'TRANSCRIPT',
  AskAI: 'ASK_AI',
};
export const $activeTab = createStore<string>(Tab.Transcript);
export const setActiveTabToTranscriptEvent = createEvent();
export const setActiveTabToAskAIEvent = createEvent();
$activeTab.on(setActiveTabToTranscriptEvent, () => Tab.Transcript);
$activeTab.on(setActiveTabToAskAIEvent, () => Tab.AskAI);

/**
 * AI Store
 */
interface AISession {
  globalId: string;
  name: string;
  messages: {
    role: string;
    content: string;
  }[];
}
export const $aiSessions = createStore<AISession[]>([]);

export const addMessageInSessionEvent = createEvent<{ globalId: string; role: string; content: string }>();
export const popMessageInSessionEvent = createEvent<{ globalId: string }>();
export const updateMessageInSessionEvent = createEvent<{ globalId: string; fragment: string }>();
export const deleteMessageEvent = createEvent<{ globalId: string; messageId: number }>();

export const deleteMessageFx = createEffect(
  async ({ orgId, globalId, messageId }: { orgId: string; globalId: string; messageId: number }) => {
    const params = { orgId, globalId, messageId };
    const response = await axiosClient.post('/v2/api/ai/sessions/messages/delete', params);
    const session = response.data.find(
      (item: { globalId: string; name: string; messages: string }) => item.globalId === globalId
    );
    let messages: { role: string; content: string }[] = [];
    if (session) {
      try {
        messages = JSON.parse(session.messages);
      } catch (e) {
        messages = [];
      }
    }
    return { globalId, messages };
  }
);

$aiSessions.on(deleteMessageFx.doneData, (sessions, { globalId, messages }) => {
  return sessions.map((session) => {
    if (session.globalId === globalId) {
      return {
        ...session,
        messages,
      };
    }
    return session;
  });
});

sample({
  clock: deleteMessageEvent,
  source: $currentWorkspace,
  fn: (workspace, { globalId, messageId }) => ({
    orgId: workspace.orgId,
    globalId,
    messageId,
  }),
  target: deleteMessageFx,
});

$aiSessions.on(addMessageInSessionEvent, (sessions, { globalId, role, content }) => {
  return sessions.map((session) => {
    if (session.globalId === globalId) {
      const updatedMessages = [...session.messages, { role, content }];
      return {
        ...session,
        messages: updatedMessages,
      };
    }
    return session;
  });
});
$aiSessions.on(popMessageInSessionEvent, (sessions, { globalId }) => {
  return sessions.map((session) => {
    if (session.globalId === globalId) {
      const updatedMessages = session.messages.slice(0, -1);
      return {
        ...session,
        messages: updatedMessages,
      };
    }
    return session;
  });
});
$aiSessions.on(updateMessageInSessionEvent, (sessions, { globalId, fragment }) => {
  return sessions.map((session) => {
    if (session.globalId === globalId) {
      const updatedSession = { ...session };
      if (updatedSession.messages && updatedSession.messages.length > 0) {
        updatedSession.messages = [...updatedSession.messages];
        const lastMessageIndex = updatedSession.messages.length - 1;
        updatedSession.messages[lastMessageIndex] = {
          ...updatedSession.messages[lastMessageIndex],
          content: `${updatedSession.messages[lastMessageIndex].content}${fragment}`,
        };
      }
      return updatedSession;
    }
    return session;
  });
});

export const loadAISessionsFx = createEffect(
  async (params: { workspaceId: string; attachmentId: string; orgId: string }): Promise<AISession[]> => {
    const response = await axiosClient.post('/v2/api/ai/sessions', params);
    const data = response.data;
    for (const i in data) {
      const item = data[i];
      let messages: { role: string; content: string }[] = [];
      try {
        messages = JSON.parse(item.messages);
      } catch (e) {
        messages = [];
      }
      item.messages = messages;
      data[i] = item;
    }
    return data;
  }
);

sample({
  source: $currentWorkspace,
  clock: startTranscriptJobFx.doneData,
  fn: (currentWorkspace, { attachmentGlobalId }) => ({
    workspaceId: currentWorkspace.globalId,
    attachmentId: attachmentGlobalId,
    orgId: currentWorkspace.orgId,
  }),
  target: loadAISessionsFx,
});

$aiSessions.on(loadAISessionsFx.doneData, (_, sessions) => sessions);

export const loadAISessionsEvent = createEvent<{ attachmentId: string }>();
sample({
  source: $currentWorkspace,
  clock: loadAISessionsEvent,
  fn: (currentWorkspace, { attachmentId }) => ({
    workspaceId: currentWorkspace.globalId,
    attachmentId: attachmentId,
    orgId: currentWorkspace.orgId,
  }),
  target: loadAISessionsFx,
});

/**
 * AI chat state
 */
interface AIChat {
  prompt: string;
  cmd: string;
  state: 'progress' | 'done';
  canceled: boolean;
}
type AIChatUpdate = Partial<AIChat>;
const aiChatInitial: AIChat = {
  prompt: '',
  cmd: '',
  state: 'done',
  canceled: false,
};
export const $aiChat = createStore<AIChat>(aiChatInitial);
export const updateAIChatEvent = createEvent<AIChatUpdate>();
$aiChat.on(updateAIChatEvent, (state, payload) => {
  if (payload.cmd && payload.cmd !== '' && state.state !== 'done') {
    return state;
  }
  return {
    ...state,
    ...payload,
  };
});
